/// <amd-module name="Repos/Views/Branches/Components/DeletedBranchesTable/DeletedBranchesTable" />
import * as React from "react";
import * as Resources from "Repos/Views/Branches/Resources";
import { DeletedBranch } from "Services/BranchesService.Types";
import { IObservableLikeValue, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { ColumnMore, ITableBreakpoint, ITableColumn, SimpleTableCell, Table, TableColumnLayout } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { VssPersona } from "VSSUI/VssPersona";
import { IDeletedBranchesTableProps } from "./DeletedBranchesTable.Props";

function getCorrectImageUrl(identity: IdentityRef): string {
    const { _links, imageUrl } = identity;

    if (_links && _links.avatar && _links.avatar.href) {
        return _links.avatar.href;
    } else if (imageUrl) {
        return imageUrl;
    } else {
        return "";
    }
}

function getColumns(props: IDeletedBranchesTableProps): ITableColumn<IObservableLikeValue<DeletedBranch | undefined>>[] {
    const { onRestore } = props;

    return [
        {
            id: "name",
            columnLayout: TableColumnLayout.singleLinePrefix,
            name: Resources.ColumnBranchHeader,
            width: new ObservableValue(-100),
            renderCell: (rowIndex, columnIndex, tableColumn, treeItem) => {
                const treeItemValue = ObservableLike.getValue(treeItem);

                let contents = null;
                if (treeItemValue) {
                    contents = <div className="body-m text-ellipsis">{treeItemValue.branchName}</div>;
                }

                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        <Icon className="icon-margin" iconName="OpenSource" />
                        {contents}
                    </SimpleTableCell>
                );
            }
        },
        {
            id: "deleted-by",
            columnLayout: TableColumnLayout.singleLinePrefix,
            name: Resources.DeletedBy,
            width: new ObservableValue(-100),
            renderCell: (rowIndex, columnIndex, tableColumn, treeItem) => {
                const treeItemValue = ObservableLike.getValue(treeItem);

                let contents = null;
                if (treeItemValue) {
                    const identity = treeItemValue.pushedBy;

                    contents = (
                        <>
                            <VssPersona
                                className="icon-margin"
                                displayName={identity.displayName}
                                imageUrl={getCorrectImageUrl(identity)}
                                size={"extra-small-plus"}
                            />
                            <Tooltip overflowOnly={true}>
                                <span className="text-ellipsis">{identity.displayName}</span>
                            </Tooltip>
                        </>
                    );
                }

                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        {contents}
                    </SimpleTableCell>
                );
            }
        },
        {
            id: "deleted-on",
            columnLayout: TableColumnLayout.singleLinePrefix,
            name: Resources.DeletedOn,
            width: new ObservableValue(-100),
            renderCell: (rowIndex, columnIndex, tableColumn, treeItem) => {
                const treeItemValue = ObservableLike.getValue(treeItem);

                let contents = null;
                if (treeItemValue) {
                    contents = <Ago date={treeItemValue.date} format={AgoFormat.Compact} />;
                }

                return (
                    <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                        {contents}
                    </SimpleTableCell>
                );
            }
        },
        new ColumnMore(treeItem => {
            const treeItemValue = ObservableLike.getValue(treeItem);

            if (!treeItemValue) {
                return { id: "deleted-menu", items: [] };
            }

            return {
                id: "deleted-menu",
                items: [
                    {
                        id: "restore",
                        onActivate: () => onRestore(treeItemValue),
                        text: "Restore branch"
                    }
                ]
            };
        })
    ];
}

// name, deleted-by, deleted-on
const TableBreakpoints: ITableBreakpoint[] = [
    {
        breakpoint: ScreenBreakpoints.xsmall,
        columnWidths: [-100, 0, 0]
    },
    {
        breakpoint: ScreenBreakpoints.small,
        columnWidths: [-40, -30, -30]
    }
];

export const DeletedBranchesTable: React.FunctionComponent<IDeletedBranchesTableProps> = props => {
    const { provider } = props;

    return (
        <Observer provider={provider}>
            {() => {
                // Don't need to inspect observerprops; we just want to trigger flips to ZeroData if it empties out
                if (provider.length > 0) {
                    return (
                        <Card
                            className="bolt-table-card bolt-card-white"
                            contentProps={{ contentPadding: false }}
                            titleProps={{ text: Resources.DeletedBranches }}
                        >
                            <Table<IObservableLikeValue<DeletedBranch | undefined>>
                                columns={getColumns(props)}
                                itemProvider={provider}
                                selectableText={true}
                                tableBreakpoints={TableBreakpoints}
                            />
                        </Card>
                    );
                } else {
                    return (
                        <div className="flex-self-center body-s secondary-text">
                            <span className="font-weight-semibold">{Resources.TipPrefix}</span>
                            <span>{Resources.FindDeletedBranches}</span>
                        </div>
                    );
                }
            }}
        </Observer>
    );
};
